import React, { useState, useRef, useEffect } from 'react';
import { FaComment, FaPaperPlane, FaTimes, FaCheck } from 'react-icons/fa';
import { Amplify } from 'aws-amplify';
import awsconfig from '../../../aws-exports';
import './FeedbackWidget.css';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { listUserProfiles } from '../../../graphql/queries';

const FeedbackWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isExpanded]);

  useEffect(() => {
    (async () => {
      try {
        const user = await getCurrentUser();
        if (!user) return;

        const attrs = await fetchUserAttributes().catch(() => ({}));
        const client = generateClient();
        let profileName = '';

        try {
          const { data } = await client.graphql({
            query: listUserProfiles,
            variables: { filter: { owner: { eq: user.username } } },
            authMode: 'userPool'
          });
          if (data?.listUserProfiles?.items?.length) {
            profileName = data.listUserProfiles.items[0].name || '';
          }
        } catch (e) {
          // silently ignore profile fetch errors
        }

        setUserName(profileName || attrs?.name || '');
        setUserEmail(attrs?.email || '');
      } catch {
        // not signed in; leave blank
      }
    })();
  }, []);

  const handleToggle = () => {
    if (isSubmitted) {
      // Reset state if already submitted
      setIsSubmitted(false);
      setFeedback('');
      setError('');
    }
    setIsExpanded(!isExpanded);
  };

  const handleCancel = () => {
    setIsExpanded(false);
    setFeedback('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Get the API endpoint from aws-exports
      const apiEndpoint = awsconfig.aws_cloud_logic_custom?.find(
        api => api.name === 'feedbackapi'
      )?.endpoint;

      if (!apiEndpoint) {
        throw new Error('Feedback service not configured');
      }

      const response = await fetch(`${apiEndpoint}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback: feedback.trim(),
          timestamp: new Date().toISOString(),
          userName: userName || undefined,
          userEmail: userEmail || undefined,
          userAgent: navigator.userAgent,
          url: window.location.href
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send feedback');
      }

      setIsSubmitted(true);
      setFeedback('');
      
      // Auto-close after success
      setTimeout(() => {
        setIsExpanded(false);
        setIsSubmitted(false);
      }, 2000);

    } catch (err) {
      console.error('Feedback error:', err);
      setError(err.message || 'Failed to send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-widget">
      {!isExpanded ? (
        <button
          className="feedback-widget__trigger"
          onClick={handleToggle}
          aria-label="Send feedback"
        >
          <FaComment className="feedback-widget__trigger-icon" />
          <span className="feedback-widget__trigger-text">Feedback</span>
        </button>
      ) : (
        <div className="feedback-widget__form-container">
          <div className="feedback-widget__header">
            <h3 className="feedback-widget__title">Send Feedback</h3>
            <button
              className="feedback-widget__close"
              onClick={handleCancel}
              aria-label="Close feedback"
            >
              <FaTimes />
            </button>
          </div>

          {isSubmitted ? (
            <div className="feedback-widget__success">
              <FaCheck className="feedback-widget__success-icon" />
              <p className="feedback-widget__success-text">
                Thank you for your feedback!
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="feedback-widget__form">
              <div className="feedback-widget__input-group">
                <label htmlFor="feedback-input" className="feedback-widget__label">
                  What's on your mind?
                </label>
                <textarea
                  id="feedback-input"
                  ref={textareaRef}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share your thoughts, report bugs, or suggest improvements..."
                  className="feedback-widget__textarea"
                  rows="4"
                  maxLength="1000"
                />
                <div className="feedback-widget__char-count">
                  {feedback.length}/1000
                </div>
              </div>

              {error && (
                <div className="feedback-widget__error">
                  {error}
                </div>
              )}

              <div className="feedback-widget__actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="feedback-widget__button feedback-widget__button--secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="feedback-widget__button feedback-widget__button--primary"
                  disabled={isSubmitting || !feedback.trim()}
                >
                  {isSubmitting ? (
                    <span className="feedback-widget__loading">
                      <div className="feedback-widget__spinner" />
                      Sending...
                    </span>
                  ) : (
                    <>
                      <FaPaperPlane className="feedback-widget__button-icon" />
                      Send Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;
